import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyOps } from '../utils/canvasOps';
import { getCanvas as getCanvasRequest, sendOps as sendOpsRequest } from '../services/CanvasServices';
import CLIENT_ID from '../utils/clientId';

/**
 * useCanvasOps — the editor's single write path, and the state it writes to.
 *
 * Every gesture becomes ops. They are applied to local state immediately so the
 * UI never waits on the network, queued, and flushed to the server in batches.
 * The server applies the same ops with the same semantics
 * (server/services/canvasOps.js), so the two never diverge in what an op means —
 * only in when it lands.
 *
 * Every batch carries the `baseVersion` it was composed against. If somebody
 * else's batch landed first the server rejects ours whole with a 409, and we
 * recover by taking the server's graph, replaying our still-unsent ops on top of
 * it, and sending again. That is deliberately not OT: two people editing the same
 * field converge on last-writer-wins, and two people editing different things
 * both keep their work, which is the level of collaboration this app needs.
 *
 * The mirror of that is `applyRemoteOps`, which the socket layer calls with
 * batches the server accepted from somebody else. It runs them through the same
 * pure appliers, so a collaborator's edit and our own take exactly the same path
 * through the graph.
 */

/** Long enough to collapse a burst of typing, short enough to feel saved. */
const FLUSH_DEBOUNCE_MS = 400;
/** Retry backoff for failures that are worth retrying (network, 5xx). */
const RETRY_DELAYS_MS = [1000, 3000, 8000];
/**
 * How many times one flush may rebase on a conflict before giving up. Each round
 * costs a GET, and needing more than a few means something is writing far faster
 * than an author can type — better to surface that than to spin.
 */
const MAX_CONFLICT_REBASES = 3;

const STALLED_MESSAGE =
    'Your latest changes have not reached the server yet. Try again in a moment.';

/** The empty draft. One definition, so no seed site can forget a slice. */
const EMPTY_DRAFT = { nodes: [], edges: [], groups: [], keys: [], tableOfContents: null };

/** Reads a fetched canvas document into the shape the appliers work on. */
const draftFromCanvas = (canvas) => ({
    nodes: canvas?.nodes || [],
    edges: canvas?.edges || [],
    groups: canvas?.groups || [],
    keys: canvas?.keys || [],
    tableOfContents: canvas?.tableOfContents ?? null,
});

/**
 * Collapses a queue so a burst of typing becomes one write instead of dozens.
 *
 * Only adjacent ops are merged, because ops are order-dependent: an
 * `updateNodeData` that follows a `removeNode` for the same id must not be
 * hoisted above it. In practice the queue is almost entirely runs of the same
 * op on the same target, which is exactly the case this catches.
 */
export function coalesceOps(ops) {
    const out = [];

    for (const op of ops) {
        const previous = out[out.length - 1];

        if (
            previous &&
            previous.type === 'updateNodeData' &&
            op.type === 'updateNodeData' &&
            previous.id === op.id
        ) {
            out[out.length - 1] = { ...previous, data: { ...previous.data, ...op.data } };
            continue;
        }

        if (
            previous &&
            previous.type === 'updateEdgeData' &&
            op.type === 'updateEdgeData' &&
            previous.id === op.id
        ) {
            out[out.length - 1] = { ...previous, data: { ...previous.data, ...op.data } };
            continue;
        }

        if (
            previous &&
            previous.type === 'updateGroup' &&
            op.type === 'updateGroup' &&
            previous.id === op.id
        ) {
            out[out.length - 1] = { ...previous, data: { ...previous.data, ...op.data } };
            continue;
        }

        if (
            previous &&
            previous.type === 'updateKey' &&
            op.type === 'updateKey' &&
            previous.id === op.id
        ) {
            out[out.length - 1] = { ...previous, data: { ...previous.data, ...op.data } };
            continue;
        }

        // The table of contents is a whole-document op, so only the last one in a
        // run says anything the ones before it did not.
        if (previous && previous.type === 'setTableOfContents' && op.type === 'setTableOfContents') {
            out[out.length - 1] = op;
            continue;
        }

        // Only the final resting place of a drag matters.
        if (previous && previous.type === 'moveNodes' && op.type === 'moveNodes') {
            const byId = new Map(previous.positions.map((entry) => [entry.id, entry]));
            for (const entry of op.positions) byId.set(entry.id, entry);
            out[out.length - 1] = { ...previous, positions: [...byId.values()] };
            continue;
        }

        out.push(op);
    }

    return out;
}

/**
 * @param {Object} params
 * @param {string} params.scenarioId
 * @param {boolean} params.enabled - False for read-only viewers; nothing is ever sent.
 * @param {Function} [params.onError] - Called with a user-facing message when a batch is permanently lost.
 */
export default function useCanvasOps({ scenarioId, enabled = true, onError } = {}) {
    const [graph, setGraphState] = useState(EMPTY_DRAFT);
    const [version, setVersion] = useState(0);
    const [publishedVersion, setPublishedVersion] = useState(0);
    const [saveState, setSaveState] = useState('idle'); // idle | pending | saving | error
    const [saveError, setSaveError] = useState(null);

    const queueRef = useRef([]);
    const timerRef = useRef(null);
    const retryIndexRef = useRef(0);
    const scenarioIdRef = useRef(scenarioId);
    const onErrorRef = useRef(onError);
    // The version every outgoing batch is composed against. Kept in a ref as well
    // as in state because a flush reads it synchronously, and React state would
    // still be showing the value from before the last response.
    const versionRef = useRef(0);

    // Every send is chained onto this, so batches can never overtake each other:
    // ops are order-dependent, and the server applies them in the order they
    // arrive. Awaiting it means "everything queued before this call has been
    // attempted", which is what publish needs before it materializes the draft.
    const chainRef = useRef(Promise.resolve());
    const saveStateRef = useRef('idle');
    // What the last flush attempt left behind, so a caller that awaited it can
    // tell whether the draft actually reached the server.
    const flushErrorRef = useRef(null);

    scenarioIdRef.current = scenarioId;
    onErrorRef.current = onError;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const scheduleFlush = useCallback((delay) => {
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            // eslint-disable-next-line no-use-before-define
            flushRef.current();
        }, delay);
    }, []);

    // `flushOps` and `scheduleFlush` refer to each other (a failed send schedules
    // a retry that calls back into the flush), so the timer reaches it by ref.
    const flushRef = useRef(() => {});

    /**
     * Takes the server's graph and replays whatever is still queued on top of it.
     *
     * This is the whole conflict story. The rejected batch is already back in the
     * queue, so re-applying the queue to server truth reproduces exactly what the
     * author sees minus anything the server has that we did not — and it is
     * important that it runs in that order, because the alternative is watching
     * your own unsent edits vanish and reappear.
     *
     * @returns {Promise<boolean>} false if the refetch failed; the caller then
     *   treats the original failure as a failure.
     */
    const rebaseOnServer = useCallback(async () => {
        try {
            const canvas = await getCanvasRequest(scenarioIdRef.current);

            versionRef.current = canvas?.version ?? 0;
            setVersion(versionRef.current);
            setPublishedVersion(canvas?.publishedVersion ?? 0);

            const server = draftFromCanvas(canvas);
            setGraphState(() => {
                try {
                    return applyOps(server, queueRef.current);
                } catch (opError) {
                    // Our own queue should always be well formed; if it is not,
                    // server truth is still the better of the two answers.
                    console.error('Could not replay pending edits after a conflict:', opError);
                    return server;
                }
            });

            return true;
        } catch (err) {
            console.error('Could not resynchronise the canvas:', err);
            return false;
        }
    }, []);

    /** Sends whatever is queued right now. Never called concurrently with itself. */
    const sendQueued = useCallback(async () => {
        if (!enabled || queueRef.current.length === 0) return;

        flushErrorRef.current = null;

        for (let rebases = 0; ; rebases += 1) {
            if (queueRef.current.length === 0) return;

            const batch = coalesceOps(queueRef.current);
            queueRef.current = [];
            setSaveState('saving');

            try {
                const result = await sendOpsRequest(scenarioIdRef.current, batch, {
                    baseVersion: versionRef.current,
                    clientId: CLIENT_ID,
                });
                retryIndexRef.current = 0;
                versionRef.current = result.version;
                setVersion(result.version);
                if (result.publishedVersion !== undefined) setPublishedVersion(result.publishedVersion);
                setSaveError(null);
                setSaveState(queueRef.current.length > 0 ? 'pending' : 'idle');

                // Anything that arrived mid-flight goes out on the next tick.
                if (queueRef.current.length > 0) scheduleFlush(FLUSH_DEBOUNCE_MS);
                return;
            } catch (err) {
                const status = err?.response?.status;

                // Anything queued while we were in flight happened AFTER this
                // batch, so a batch that will be tried again goes back at the
                // front. A batch we are giving up on is NOT requeued — it would
                // otherwise ride along on every later flush, failing each time.
                const requeue = () => {
                    queueRef.current = [...batch, ...queueRef.current];
                };
                const dropRequeued = () => {
                    queueRef.current = queueRef.current.slice(batch.length);
                };

                /** Back off and try the (already requeued) batch again. */
                const backOff = () => {
                    setSaveState('pending');
                    flushErrorRef.current = STALLED_MESSAGE;
                    scheduleFlush(RETRY_DELAYS_MS[retryIndexRef.current++]);
                };

                const retriesLeft = retryIndexRef.current < RETRY_DELAYS_MS.length;

                // 409 — somebody else's batch landed between our read and our
                // write. Nothing of ours was applied, so rebase and send again
                // inside this same flush: a caller awaiting it (publish) has to
                // get a truthful answer about whether the draft is on the server.
                if (status === 409 && rebases < MAX_CONFLICT_REBASES) {
                    requeue();
                    if (await rebaseOnServer()) continue;

                    // The refetch itself failed, so the network is down rather
                    // than the write conflicting. The batch is already requeued.
                    if (retriesLeft) {
                        backOff();
                        return;
                    }
                    dropRequeued();
                }

                const isRetryable = !status || status >= 500 || status === 429;
                if (isRetryable && retriesLeft) {
                    requeue();
                    backOff();
                    return;
                }

                // Permanently rejected (403, 400, 413, or out of retries). Local
                // state is now ahead of the server and we cannot silently pretend
                // otherwise — the author has to know their work did not save.
                const message =
                    err?.response?.data?.message ||
                    'Your latest changes could not be saved. Reload the page to see the last saved version.';
                retryIndexRef.current = 0;
                flushErrorRef.current = message;
                setSaveError(message);
                setSaveState('error');
                if (onErrorRef.current) onErrorRef.current(message);
                return;
            }
        }
    }, [enabled, scheduleFlush, rebaseOnServer]);

    /**
     * Flushes the queue, waiting for any send already in progress.
     *
     * The returned promise resolves once everything queued at call time has been
     * attempted, to `null` if it all landed or to a message if it did not —
     * publish awaits it so it can never materialize a draft the server has not
     * seen.
     */
    const flushOps = useCallback(() => {
        clearTimer();
        if (!enabled) return Promise.resolve(null);

        chainRef.current = chainRef.current
            .then(sendQueued, sendQueued)
            .then(() => flushErrorRef.current);
        return chainRef.current;
    }, [enabled, sendQueued]);

    flushRef.current = flushOps;

    /**
     * Applies ops locally and queues them for the server.
     *
     * @param {Array<Object>|Object} ops
     * @param {{immediate?: boolean}} [options] - `immediate` skips the debounce,
     *   for gestures whose result must be durable before the next thing happens.
     */
    const dispatchOps = useCallback(
        (ops, { immediate = false } = {}) => {
            const batch = Array.isArray(ops) ? ops : [ops];
            if (batch.length === 0) return;

            setGraphState((current) => applyOps(current, batch));

            if (!enabled) return;

            queueRef.current.push(...batch);
            setSaveState((state) => (state === 'saving' ? state : 'pending'));

            if (immediate) {
                flushOps();
                return;
            }

            // Restart the debounce: a burst of typing collapses into one write.
            clearTimer();
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                flushOps();
            }, FLUSH_DEBOUNCE_MS);
        },
        [enabled, flushOps]
    );

    /**
     * Replaces the whole graph — initial load and post-publish refetch only.
     * Never a substitute for an op: this does not reach the server.
     */
    const setGraph = useCallback((updater) => {
        setGraphState((current) =>
            typeof updater === 'function' ? updater(current) : draftFromCanvas(updater)
        );
    }, []);

    /** Seeds state from a freshly fetched canvas document. */
    const loadCanvasDocument = useCallback((canvas) => {
        setGraphState(draftFromCanvas(canvas));
        versionRef.current = canvas?.version ?? 0;
        setVersion(versionRef.current);
        setPublishedVersion(canvas?.publishedVersion ?? 0);
        queueRef.current = [];
        setSaveError(null);
        setSaveState('idle');
    }, []);

    /**
     * Applies an op batch the server accepted from another editor.
     *
     * Three things decide what happens, all from the version the batch produced:
     *
     *   - `version <= ours` — we already have it (our own echo, or a duplicate
     *     after a reconnect). Ignore.
     *   - `version === ours + 1` — the next batch in the sequence. Apply it.
     *   - anything higher — we missed a batch, so applying this one would leave a
     *     hole in the graph that nothing later would fix. Refetch instead.
     *
     * Remote ops land on top of our own un-flushed edits rather than underneath
     * them, which is not what the server will end up storing if both touch the
     * same field. That is the accepted limit of this design: fields converge on
     * last-writer-wins, and the version check guarantees we notice when we are
     * genuinely out of sync.
     */
    const applyRemoteOps = useCallback(
        ({ ops, version: remoteVersion, publishedVersion: remotePublished } = {}) => {
            if (!Array.isArray(ops) || ops.length === 0) return;

            if (typeof remoteVersion === 'number') {
                if (remoteVersion <= versionRef.current) return;

                if (remoteVersion > versionRef.current + 1) {
                    // Serialised behind any send already in flight, so the refetch
                    // cannot read the canvas from before our own pending write.
                    chainRef.current = chainRef.current.then(rebaseOnServer, rebaseOnServer);
                    return;
                }
            }

            setGraphState((current) => {
                try {
                    return applyOps(current, ops);
                } catch (err) {
                    console.error('Ignoring an unusable collaborator batch:', err);
                    return current;
                }
            });

            if (typeof remoteVersion === 'number') {
                versionRef.current = remoteVersion;
                setVersion(remoteVersion);
            }
            if (typeof remotePublished === 'number') setPublishedVersion(remotePublished);
        },
        [rebaseOnServer]
    );

    // Leaving the editor with edits still debounced would silently lose them.
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (queueRef.current.length === 0 && saveStateRef.current !== 'saving') return;
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            clearTimer();
            // Unmounting is an in-app navigation; there is still time to send.
            if (queueRef.current.length > 0) flushOps();
        };
    }, [flushOps]);

    saveStateRef.current = saveState;

    const hasUnsavedOps = saveState === 'pending' || saveState === 'saving';

    // `version > publishedVersion` IS the "unpublished changes" signal. Nothing
    // tracks a dirty flag; it is a pure derivation from what the server returned.
    const hasUnpublishedChanges = version > publishedVersion;

    return useMemo(
        () => ({
            nodes: graph.nodes,
            edges: graph.edges,
            groups: graph.groups,
            keys: graph.keys,
            tableOfContents: graph.tableOfContents,
            version,
            publishedVersion,
            hasUnpublishedChanges,
            saveState,
            saveError,
            hasUnsavedOps,
            dispatchOps,
            flushOps,
            setGraph,
            loadCanvasDocument,
            applyRemoteOps,
            setPublishedVersion,
        }),
        [
            graph.nodes,
            graph.edges,
            graph.groups,
            graph.keys,
            graph.tableOfContents,
            version,
            publishedVersion,
            hasUnpublishedChanges,
            saveState,
            saveError,
            hasUnsavedOps,
            dispatchOps,
            flushOps,
            setGraph,
            loadCanvasDocument,
            applyRemoteOps,
        ]
    );
}
